export interface RealStanding {
  position: number;
  team: string;
  shortName: string;
  crest: string;
  teamId: string;
  played: number;
  won: number;
  draw: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
  points: number;
  form: string[];
  homeRecord?: string;
  league?: string;
  description?: string;
}

interface FootballTableProps {
  data: RealStanding[];
  selectedId?: number;
  onSelect?: (team: RealStanding) => void;
}

function FormBadge({ r }: { r: string }) {
  const cls =
    r === 'W' ? 'bg-[#00e87a]/20 text-[#00e87a]' :
    r === 'L' ? 'bg-[#ef4444]/20 text-[#ef4444]' :
    'bg-[#f59e0b]/20 text-[#f59e0b]';
  return (
    <span className={`w-5 h-5 flex items-center justify-center rounded text-xs font-black ${cls}`}>
      {r}
    </span>
  );
}

// Computed free stats from real goals data
function computeXG(goalsFor: number, played: number): string {
  if (played === 0) return '—';
  const avg = goalsFor / played;
  return Math.max(0.4, avg * 0.88 + 0.12).toFixed(2);
}

function computeCorners(goalsFor: number, played: number): string {
  if (played === 0) return '—';
  const avg = goalsFor / played;
  return Math.max(3.5, 4.1 + avg * 0.72).toFixed(1);
}

export default function FootballTable({ data, selectedId, onSelect }: FootballTableProps) {
  return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[#2a2d3a] flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          Clasificación
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#00e87a]/10 text-[#00e87a] text-[10px] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00e87a] animate-pulse inline-block"></span>
            Tiempo real · ESPN
          </span>
        </h3>
        <span className="text-[#6b7280] text-xs">{data.length} equipos</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[780px]">
          <thead>
            <tr className="border-b border-[#2a2d3a]">
              <th className="w-10 px-4 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase tracking-wide">#</th>
              <th className="px-5 py-3 text-left text-[#6b7280] text-xs font-semibold uppercase tracking-wide">Equipo</th>
              <th className="px-3 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">PJ</th>
              <th className="px-3 py-3 text-center text-[#00e87a] text-xs font-semibold uppercase">PG</th>
              <th className="px-3 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">PE</th>
              <th className="px-3 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">PP</th>
              <th className="px-3 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">GF</th>
              <th className="px-3 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">GC</th>
              <th className="px-3 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">DG</th>
              <th className="px-3 py-3 text-center text-[#ffd700] text-xs font-semibold uppercase">Pts</th>
              {/* Free: xG and Corners estimated */}
              <th className="px-3 py-3 text-center text-[#ff6b35] text-xs font-semibold uppercase" title="Goles esperados por partido (estimado)">
                xG/P
              </th>
              <th className="px-3 py-3 text-center text-[#ff6b35] text-xs font-semibold uppercase" title="Corners por partido (estimado)">
                C/P
              </th>
              <th className="px-4 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">Forma</th>
            </tr>
          </thead>
          <tbody>
            {data.map((team, i) => {
              const isSelected = selectedId === team.position;
              const posColor =
                i === 0 ? 'text-[#ffd700]' :
                i < 4 ? 'text-[#00e87a]' :
                i < 6 ? 'text-[#a8b2c1]' :
                'text-[#3d4149]';

              const xg = computeXG(team.goalsFor, team.played);
              const corners = computeCorners(team.goalsFor, team.played);
              const xgNum = parseFloat(xg);
              const cNum = parseFloat(corners);

              return (
                <tr
                  key={`${team.teamId}-${i}`}
                  onClick={() => onSelect?.(team)}
                  className={`border-b border-[#1e2028] transition-colors cursor-pointer ${
                    isSelected
                      ? 'bg-[#00e87a]/8 border-l-2 border-l-[#00e87a]'
                      : i === 0
                      ? 'bg-[#ffd700]/3 hover:bg-[#ffd700]/5'
                      : 'hover:bg-white/[0.02]'
                  }`}
                >
                  <td className="px-4 py-3.5 text-center">
                    <span className={`text-sm font-black ${posColor}`}>{team.position}</span>
                  </td>

                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      {team.crest ? (
                        <img
                          src={team.crest}
                          alt={team.team}
                          className="w-6 h-6 object-contain flex-shrink-0"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-6 h-6 rounded bg-[#2a2d3a] flex items-center justify-center flex-shrink-0">
                          <span className="text-[#6b7280] text-[10px] font-bold">{team.shortName.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <p translate="no" className="text-white text-sm font-semibold leading-tight">{team.team}</p>
                        {team.league && <p translate="no" className="text-[#6b7280] text-xs">{team.league}</p>}
                      </div>
                    </div>
                  </td>

                  <td className="px-3 py-3.5 text-center text-[#a8b2c1] text-sm tabular-nums">{team.played}</td>
                  <td className="px-3 py-3.5 text-center text-[#00e87a] text-sm font-bold tabular-nums bg-[#00e87a]/3">{team.won}</td>
                  <td className="px-3 py-3.5 text-center text-[#f59e0b] text-sm tabular-nums">{team.draw}</td>
                  <td className="px-3 py-3.5 text-center text-[#ef4444] text-sm tabular-nums">{team.lost}</td>
                  <td className="px-3 py-3.5 text-center text-[#a8b2c1] text-sm tabular-nums">{team.goalsFor}</td>
                  <td className="px-3 py-3.5 text-center text-[#6b7280] text-sm tabular-nums">{team.goalsAgainst}</td>
                  <td className="px-3 py-3.5 text-center text-sm font-bold tabular-nums">
                    <span className={team.goalDiff > 0 ? 'text-[#00e87a]' : team.goalDiff < 0 ? 'text-[#ef4444]' : 'text-[#6b7280]'}>
                      {team.goalDiff > 0 ? `+${team.goalDiff}` : team.goalDiff}
                    </span>
                  </td>
                  <td className="px-3 py-3.5 text-center bg-[#ffd700]/3">
                    <span className="text-[#ffd700] text-sm font-black tabular-nums">{team.points}</span>
                  </td>

                  {/* xG/P estimated */}
                  <td className="px-3 py-3.5 text-center bg-[#ff6b35]/3">
                    <span className={`text-xs font-bold tabular-nums ${xgNum >= 2 ? 'text-[#ff6b35]' : xgNum >= 1.3 ? 'text-[#f59e0b]' : 'text-[#a8b2c1]'}`}>
                      {xg}
                    </span>
                  </td>

                  {/* Corners/P estimated */}
                  <td className="px-3 py-3.5 text-center bg-[#ff6b35]/3">
                    <span className={`text-xs font-bold tabular-nums ${cNum >= 6 ? 'text-[#ff6b35]' : cNum >= 5 ? 'text-[#f59e0b]' : 'text-[#a8b2c1]'}`}>
                      {corners}
                    </span>
                  </td>

                  {/* Form */}
                  <td className="px-4 py-3.5">
                    <div className="flex items-center justify-center gap-0.5">
                      {team.form.length > 0
                        ? team.form.map((r, j) => <FormBadge key={j} r={r} />)
                        : <span className="text-[#3d4149] text-xs">—</span>
                      }
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3 border-t border-[#1e2028] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1 text-[#6b7280] text-[10px]">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#00e87a]/20 inline-block"></span>PG = Victorias
          </span>
          <span className="flex items-center gap-1 text-[#6b7280] text-[10px]">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#ffd700]/20 inline-block"></span>Pts = Puntos
          </span>
          <span className="flex items-center gap-1 text-[#6b7280] text-[10px]">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#ff6b35]/20 inline-block"></span>xG/P · C/P = estimados basados en goles
          </span>
        </div>
        <span className="text-[#3d4149] text-[10px]">Click en un equipo para detalles</span>
      </div>
    </div>
  );
}
